import { useState } from 'react';
import { LogOut, Mail, Shield, User as UserIcon } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  getScopePreference,
  setScopePreference,
  type SessionScope,
} from '../lib/scopePreference';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [scope, setScopeState] = useState<SessionScope>(() => getScopePreference());

  const handleScopeChange = (next: SessionScope) => {
    setScopeState(next);
    setScopePreference(next);
  };

  if (!user) return null;

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-6 p-4">
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-bark">
            Signed in as
          </h2>
          <div className="rounded-lg border border-fog bg-white/60 p-4 shadow-sm">
            <dl className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <UserIcon className="mt-0.5 h-4 w-4 text-bark" aria-hidden />
                <div>
                  <dt className="text-xs text-bark">Name</dt>
                  <dd className="font-medium text-espresso">{user.name}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 text-bark" aria-hidden />
                <div>
                  <dt className="text-xs text-bark">Email</dt>
                  <dd className="font-medium text-espresso">{user.email}</dd>
                </div>
              </div>
              {user.isAdmin && (
                <div className="flex items-start gap-3">
                  <Shield className="mt-0.5 h-4 w-4 text-gold" aria-hidden />
                  <div>
                    <dt className="text-xs text-bark">Access</dt>
                    <dd className="font-medium text-gold">Admin</dd>
                  </div>
                </div>
              )}
            </dl>
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-bark">
            Default scope
          </h2>
          <div className="rounded-lg border border-fog bg-white/60 p-4 shadow-sm">
            <div className="grid grid-cols-3 overflow-hidden rounded-md">
              <button
                type="button"
                onClick={() => handleScopeChange('personal_life')}
                aria-pressed={scope === 'personal_life'}
                className={
                  'px-4 py-3 text-sm font-medium transition-colors ' +
                  (scope === 'personal_life'
                    ? 'bg-gold text-ivory'
                    : 'bg-white/60 text-espresso hover:bg-fog/40')
                }
              >
                Personal
              </button>
              <button
                type="button"
                onClick={() => handleScopeChange('everfen_business')}
                aria-pressed={scope === 'everfen_business'}
                className={
                  'border-l border-fog px-4 py-3 text-sm font-medium transition-colors ' +
                  (scope === 'everfen_business'
                    ? 'bg-gold text-ivory'
                    : 'bg-white/60 text-espresso hover:bg-fog/40')
                }
              >
                Business
              </button>
              <button
                type="button"
                onClick={() => handleScopeChange('builds')}
                aria-pressed={scope === 'builds'}
                className={
                  'border-l border-fog px-4 py-3 text-sm font-medium transition-colors ' +
                  (scope === 'builds'
                    ? 'bg-gold text-ivory'
                    : 'bg-white/60 text-espresso hover:bg-fog/40')
                }
              >
                Builds
              </button>
            </div>
            <p className="mt-3 text-xs text-bark">
              New chats are created in this scope. A session's scope is fixed once
              created — changing this only affects future new chats.
            </p>
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-bark">
            Session
          </h2>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-fog bg-white/60 px-4 py-3 text-sm font-medium text-espresso shadow-sm transition-colors hover:bg-fog/40 disabled:opacity-60"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </section>
      </div>
    </div>
  );
}
